import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';

const api = axios.create({ baseURL: API_URL });

const ErrorLogScreen = () => {
  const { isAdmin, token } = useAuth();

  // Filter state
  const [severity, setSeverity] = useState('');
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 30;

  // Checkpoint filter
  const [sinceCheckpoint, setSinceCheckpoint] = useState(false);
  const [checkpointNote, setCheckpointNote] = useState('');
  const [showCheckpointInput, setShowCheckpointInput] = useState(false);
  const [settingCheckpoint, setSettingCheckpoint] = useState(false);

  // Data state
  const [errors, setErrors] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);

  // Set up auth header
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  // Fetch errors
  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (severity) params.severity = severity;
      if (source) params.source = source;
      if (search) params.search = search;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (sinceCheckpoint) params.since_checkpoint = 'true';

      const response = await api.get('/errors', { params });
      setErrors(response.data.errors || []);
      setPagination(response.data.pagination || { total: 0, totalPages: 0 });
    } catch (err) {
      console.error('Failed to fetch errors:', err);
    } finally {
      setLoading(false);
    }
  }, [page, severity, source, search, startDate, endDate, sinceCheckpoint]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/errors/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch error stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Reset to page 1 when filters change
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchErrors();
  };

  const handleClearFilters = () => {
    setSeverity('');
    setSource('');
    setSearch('');
    setStartDate('');
    setEndDate('');
    setSinceCheckpoint(false);
    setPage(1);
  };

  // Set a new checkpoint
  const handleSetCheckpoint = async () => {
    setSettingCheckpoint(true);
    try {
      await api.post('/errors/checkpoint', { note: checkpointNote || undefined });
      setCheckpointNote('');
      setShowCheckpointInput(false);
      // Refresh stats and errors
      fetchStats();
      fetchErrors();
    } catch (err) {
      console.error('Failed to set checkpoint:', err);
      alert('Failed to set checkpoint');
    } finally {
      setSettingCheckpoint(false);
    }
  };

  // Toggle since-checkpoint filter
  const handleToggleSinceCheckpoint = () => {
    setSinceCheckpoint(prev => !prev);
    setPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric',
      minute: '2-digit', hour12: true
    });
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true
    });
  };

  const severityColor = (sev) => {
    switch (sev) {
      case 'error': return { bg: '#fff5f5', text: '#c53030', border: '#fed7d7' };
      case 'warn': return { bg: '#fffff0', text: '#b7791f', border: '#fefcbf' };
      case 'info': return { bg: '#ebf8ff', text: '#2b6cb0', border: '#bee3f8' };
      case 'checkpoint': return { bg: '#f0fff4', text: '#276749', border: '#c6f6d5' };
      default: return { bg: '#f7fafc', text: '#4a5568', border: '#e2e8f0' };
    }
  };

  if (!isAdmin) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Admin access required</div>;
  }

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#333', margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>
            Error Log
          </h1>
          <p style={{ color: '#888', fontSize: '0.82rem', margin: 0 }}>
            Monitor API and frontend errors across the system
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {showCheckpointInput ? (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                type="text"
                value={checkpointNote}
                onChange={(e) => setCheckpointNote(e.target.value)}
                placeholder="Optional note..."
                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #c6f6d5', fontSize: '0.8rem', width: '180px' }}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSetCheckpoint()}
              />
              <button
                onClick={handleSetCheckpoint}
                disabled={settingCheckpoint}
                style={{
                  padding: '6px 14px', borderRadius: '6px', border: 'none',
                  backgroundColor: '#276749', color: 'white', fontSize: '0.8rem',
                  fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap'
                }}
              >
                {settingCheckpoint ? 'Setting...' : 'Confirm'}
              </button>
              <button
                onClick={() => { setShowCheckpointInput(false); setCheckpointNote(''); }}
                style={{
                  padding: '6px 10px', borderRadius: '6px', border: '1px solid #e9ecef',
                  backgroundColor: 'white', color: '#555', fontSize: '0.8rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowCheckpointInput(true)}
              style={{
                padding: '7px 16px', borderRadius: '6px', border: '1px solid #c6f6d5',
                backgroundColor: '#f0fff4', color: '#276749', fontSize: '0.82rem',
                fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              🚩 Set Checkpoint
            </button>
          )}
        </div>
      </div>

      {/* Stats Dashboard */}
      {stats?.summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          <StatCard label="Last 24h" value={stats.summary.last_24h} color="#c53030" />
          <StatCard label="Errors (24h)" value={stats.summary.errors_24h} color="#e53e3e" />
          <StatCard label="Warnings (24h)" value={stats.summary.warnings_24h} color="#d69e2e" />
          <StatCard label="Last 7 days" value={stats.summary.last_7d} color="#2563eb" />
          <StatCard label="Last 30 days" value={stats.summary.last_30d} color="#555" />
          {stats.lastCheckpoint && (
            <div
              onClick={handleToggleSinceCheckpoint}
              style={{
                backgroundColor: sinceCheckpoint ? '#f0fff4' : 'white', borderRadius: '8px',
                border: sinceCheckpoint ? '2px solid #276749' : '1px solid #e9ecef',
                padding: '14px 16px', textAlign: 'center', cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#276749', lineHeight: 1 }}>
                {stats.sinceCheckpoint || 0}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#276749', marginTop: '4px', fontWeight: sinceCheckpoint ? '600' : '400' }}>
                Since Checkpoint
              </div>
            </div>
          )}
        </div>
      )}

      {/* Top Errors (last 24h) */}
      {stats?.topErrors?.length > 0 && (
        <div style={{
          backgroundColor: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '8px',
          padding: '16px', marginBottom: '24px'
        }}>
          <h3 style={{ fontSize: '0.85rem', fontWeight: '600', color: '#c53030', margin: '0 0 10px 0' }}>
            🔥 Top Errors (Last 24h)
          </h3>
          {stats.topErrors.slice(0, 5).map((e, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '6px 0', borderBottom: i < Math.min(stats.topErrors.length, 5) - 1 ? '1px solid #fed7d7' : 'none'
            }}>
              <span style={{ fontSize: '0.8rem', color: '#742a2a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '12px' }}>
                [{e.source}] {e.message}
              </span>
              <span style={{
                backgroundColor: '#c53030', color: 'white', borderRadius: '10px',
                padding: '2px 8px', fontSize: '0.7rem', fontWeight: '600', flexShrink: 0
              }}>
                ×{e.count}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Last Checkpoint Banner */}
      {stats?.lastCheckpoint && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#f0fff4', border: '1px solid #c6f6d5', borderRadius: '8px',
          padding: '10px 16px', marginBottom: '16px'
        }}>
          <span style={{ fontSize: '1rem' }}>🚩</span>
          <span style={{ fontSize: '0.8rem', color: '#276749', flex: 1 }}>
            <strong>Last checkpoint:</strong> {formatDateFull(stats.lastCheckpoint.created_at)}
            {stats.lastCheckpoint.message && (
              <span style={{ color: '#48bb78', marginLeft: '8px' }}>— {stats.lastCheckpoint.message}</span>
            )}
          </span>
          {!sinceCheckpoint && (
            <button
              onClick={handleToggleSinceCheckpoint}
              style={{
                padding: '4px 12px', borderRadius: '4px', border: '1px solid #276749',
                backgroundColor: 'transparent', color: '#276749', fontSize: '0.75rem',
                fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap'
              }}
            >
              Show only new errors
            </button>
          )}
          {sinceCheckpoint && (
            <button
              onClick={handleToggleSinceCheckpoint}
              style={{
                padding: '4px 12px', borderRadius: '4px', border: '1px solid #276749',
                backgroundColor: '#276749', color: 'white', fontSize: '0.75rem',
                fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap'
              }}
            >
              ✓ Filtering since checkpoint
            </button>
          )}
        </div>
      )}

      {/* Filters */}
      <form onSubmit={handleSearchSubmit} style={{
        display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px',
        padding: '16px', backgroundColor: 'white', borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <select value={severity} onChange={handleFilterChange(setSeverity)} style={filterSelectStyle}>
          <option value="">All Severities</option>
          <option value="error">Error</option>
          <option value="warn">Warning</option>
          <option value="info">Info</option>
        </select>

        <select value={source} onChange={handleFilterChange(setSource)} style={filterSelectStyle}>
          <option value="">All Sources</option>
          <option value="api">API</option>
          <option value="frontend">Frontend</option>
          <option value="middleware">Middleware</option>
        </select>

        <input
          type="date"
          value={startDate}
          onChange={handleFilterChange(setStartDate)}
          style={filterInputStyle}
          placeholder="Start date"
        />
        <input
          type="date"
          value={endDate}
          onChange={handleFilterChange(setEndDate)}
          style={filterInputStyle}
          placeholder="End date"
        />

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search messages, URLs, codes..."
          style={{ ...filterInputStyle, flex: '1 1 200px' }}
        />

        <button type="submit" style={filterButtonStyle}>
          Search
        </button>
        <button type="button" onClick={handleClearFilters} style={clearButtonStyle}>
          Clear
        </button>
      </form>

      {/* Results count */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ fontSize: '0.8rem', color: '#888' }}>
          {pagination.total} error{pagination.total !== 1 ? 's' : ''} found          {sinceCheckpoint && ' since last checkpoint'}          {pagination.totalPages > 1 && ` • Page ${page} of ${pagination.totalPages}`}
        </span>
        <button onClick={() => { fetchErrors(); fetchStats(); }} style={{
          background: 'none', border: 'none', color: '#2563eb', fontSize: '0.8rem',
          cursor: 'pointer', fontWeight: '500'
        }}>
          ↻ Refresh
        </button>
      </div>

      {/* Error List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>
      ) : errors.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px', backgroundColor: 'white',
          borderRadius: '8px', border: '1px solid #e9ecef'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>✅</div>
          <p style={{ color: '#555', fontSize: '0.9rem' }}>No errors found</p>
          <p style={{ color: '#888', fontSize: '0.8rem' }}>That's a good sign!</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {errors.map((err) => {
            const sc = severityColor(err.severity);
            const isSelected = selectedError?.error_id === err.error_id;

            // Checkpoint rows render as a distinct banner/divider
            if (err.severity === 'checkpoint') {
              return (
                <div key={err.error_id} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 16px',
                  background: 'linear-gradient(90deg, #f0fff4 0%, #c6f6d5 50%, #f0fff4 100%)',
                  borderRadius: '8px', border: '1px dashed #68d391'
                }}>
                  <span style={{ fontSize: '0.9rem' }}>🚩</span>
                  <span style={{
                    flex: 1, fontSize: '0.8rem', color: '#276749', fontWeight: '600'
                  }}>
                    {err.message}
                  </span>
                  <span style={{ fontSize: '0.72rem', color: '#48bb78', flexShrink: 0 }}>
                    {formatDateFull(err.created_at)}
                  </span>
                </div>
              );
            }

            return (
              <div key={err.error_id}>
                {/* Summary row */}
                <div
                  onClick={() => setSelectedError(isSelected ? null : err)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '12px 16px', backgroundColor: 'white',
                    borderRadius: isSelected ? '8px 8px 0 0' : '8px',
                    border: `1px solid ${isSelected ? '#2563eb' : '#e9ecef'}`,
                    borderBottom: isSelected ? '1px dashed #dee2e6' : undefined,
                    cursor: 'pointer', transition: 'border-color 0.15s ease'
                  }}
                >
                  {/* Severity badge */}
                  <span style={{
                    backgroundColor: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                    borderRadius: '4px', padding: '2px 8px', fontSize: '0.7rem',
                    fontWeight: '600', textTransform: 'uppercase', flexShrink: 0,
                    minWidth: '46px', textAlign: 'center'
                  }}>
                    {err.severity}
                  </span>

                  {/* Source */}
                  <span style={{
                    backgroundColor: '#f0f4ff', color: '#2563eb', borderRadius: '4px',
                    padding: '2px 8px', fontSize: '0.7rem', fontWeight: '500', flexShrink: 0
                  }}>
                    {err.source}
                  </span>

                  {/* Message */}
                  <span style={{
                    flex: 1, fontSize: '0.82rem', color: '#333', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {err.message}
                  </span>

                  {/* Request method + URL (if API error) */}
                  {err.request_url && (
                    <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {err.request_method} {err.request_url}
                    </span>
                  )}

                  {/* Timestamp */}
                  <span style={{ fontSize: '0.72rem', color: '#aaa', flexShrink: 0, minWidth: '90px', textAlign: 'right' }}>
                    {formatDate(err.created_at)}
                  </span>
                </div>

                {/* Detail panel (expanded) */}
                {isSelected && (
                  <div style={{
                    padding: '16px', backgroundColor: '#fafbfc',
                    border: '1px solid #2563eb', borderTop: 'none',
                    borderRadius: '0 0 8px 8px'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                      <DetailField label="Error ID" value={err.error_id} />
                      <DetailField label="Timestamp" value={formatDateFull(err.created_at)} />
                      <DetailField label="Severity" value={err.severity} />
                      <DetailField label="Source" value={err.source} />
                      {err.error_code && <DetailField label="Error Code" value={err.error_code} />}
                      {err.status_code && <DetailField label="HTTP Status" value={err.status_code} />}
                      {err.username && <DetailField label="User" value={`${err.username} (ID: ${err.user_id})`} />}
                      {err.ip_address && <DetailField label="IP Address" value={err.ip_address} />}
                      {err.request_method && <DetailField label="Request" value={`${err.request_method} ${err.request_url}`} />}
                      {err.page_url && <DetailField label="Page URL" value={err.page_url} />}
                    </div>

                    <DetailField label="Message" value={err.message} fullWidth />

                    {err.stack_trace && (
                      <div style={{ marginTop: '12px' }}>
                        <label style={detailLabelStyle}>Stack Trace</label>
                        <pre style={preStyle}>{err.stack_trace}</pre>
                      </div>
                    )}

                    {err.component_stack && (
                      <div style={{ marginTop: '12px' }}>
                        <label style={detailLabelStyle}>Component Stack</label>
                        <pre style={preStyle}>{err.component_stack}</pre>
                      </div>
                    )}

                    {err.request_body && Object.keys(err.request_body).length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <label style={detailLabelStyle}>Request Body</label>
                        <pre style={preStyle}>{JSON.stringify(err.request_body, null, 2)}</pre>
                      </div>
                    )}

                    {err.user_agent && (
                      <div style={{ marginTop: '12px' }}>
                        <DetailField label="User Agent" value={err.user_agent} fullWidth />
                      </div>
                    )}

                    {err.metadata && Object.keys(err.metadata).length > 0 && (
                      <div style={{ marginTop: '12px' }}>
                        <label style={detailLabelStyle}>Metadata</label>
                        <pre style={preStyle}>{JSON.stringify(err.metadata, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={paginationButtonStyle(page === 1)}
          >
            ← Prev
          </button>
          {generatePageNumbers(page, pagination.totalPages).map((p, i) => (
            p === '...' ? (
              <span key={`dots-${i}`} style={{ padding: '6px 4px', color: '#888', fontSize: '0.8rem' }}>…</span>
            ) : (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  ...paginationButtonStyle(false),
                  backgroundColor: p === page ? '#2563eb' : 'white',
                  color: p === page ? 'white' : '#333',
                  borderColor: p === page ? '#2563eb' : '#e9ecef',
                  fontWeight: p === page ? '600' : '400'
                }}
              >
                {p}
              </button>
            )
          ))}
          <button
            onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
            style={paginationButtonStyle(page === pagination.totalPages)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────

const StatCard = ({ label, value, color }) => (
  <div style={{
    backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e9ecef',
    padding: '14px 16px', textAlign: 'center'
  }}>
    <div style={{ fontSize: '1.5rem', fontWeight: '700', color, lineHeight: 1 }}>{value || 0}</div>
    <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '4px' }}>{label}</div>
  </div>
);

const DetailField = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label style={detailLabelStyle}>{label}</label>
    <div style={{ fontSize: '0.82rem', color: '#333', wordBreak: 'break-word' }}>{value}</div>
  </div>
);

// ─── Style constants ─────────────────────────────────────────────

const detailLabelStyle = {
  display: 'block', fontSize: '0.7rem', color: '#888',
  fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em',
  marginBottom: '2px'
};

const preStyle = {
  backgroundColor: '#1a202c', color: '#e2e8f0', borderRadius: '6px',
  padding: '12px', fontSize: '0.72rem', lineHeight: '1.5',
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  maxHeight: '300px', overflow: 'auto', margin: 0
};

const filterSelectStyle = {
  padding: '7px 10px', borderRadius: '6px', border: '1px solid #e9ecef',
  fontSize: '0.82rem', color: '#333', backgroundColor: 'white', cursor: 'pointer'
};

const filterInputStyle = {
  padding: '7px 10px', borderRadius: '6px', border: '1px solid #e9ecef',
  fontSize: '0.82rem', color: '#333'
};

const filterButtonStyle = {
  padding: '7px 18px', borderRadius: '6px', border: 'none',
  backgroundColor: '#2563eb', color: 'white', fontSize: '0.82rem',
  fontWeight: '500', cursor: 'pointer'
};

const clearButtonStyle = {
  padding: '7px 18px', borderRadius: '6px', border: '1px solid #e9ecef',
  backgroundColor: 'white', color: '#555', fontSize: '0.82rem',
  fontWeight: '500', cursor: 'pointer'
};

const paginationButtonStyle = (disabled) => ({
  padding: '6px 14px', borderRadius: '6px', border: '1px solid #e9ecef',
  backgroundColor: 'white', color: disabled ? '#ccc' : '#333',
  fontSize: '0.8rem', fontWeight: '500', cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1
});

// Generate smart page number array with ellipsis
function generatePageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  pages.push(1);
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

export default ErrorLogScreen;
